import type {
  QuoteFragmentAllocation,
  QuoteSourceFragment,
} from '../../../domain/quoteSourceFragment';
import {
  allocateFragmentCents,
  assertFragmentAllocation,
  assertSourceFragment,
  fragmentMemberKey,
} from '../../../domain/quoteSourceFragment';
import { quoteTableGroupKey } from './quoteTableGroups';
import type { CpqState, QuoteItem } from './types';

export function fragmentInstanceItems(
  items: readonly QuoteItem[],
  item: QuoteItem,
): QuoteItem[] {
  return item.sourceFragment
    ? items.filter(
        (entry) =>
          entry.sourceFragment?.instanceKey ===
          item.sourceFragment?.instanceKey,
      )
    : [];
}

/** Validates persisted slice facts without inferring money from a display unit. */
export function assertQuoteFragmentInstances(
  items: readonly QuoteItem[],
): void {
  const instances = new Map<string, QuoteItem[]>();
  for (const item of items) {
    if (!item.sourceFragment) {
      if (item.fragmentAllocation) throw new Error('分行金额缺少正式来源合同');
      continue;
    }
    assertSourceFragment(item.sourceFragment);
    if (
      item.sourceFragment.mode !== 'quote-customized' &&
      item.sourceFragment.memberKey !== fragmentMemberKey(item)
    )
      throw new Error('分行成员与准确来源不一致');
    if (item.sourceFragment.mode === 'quote-customized') {
      if (
        !item.containerInstanceId ||
        item.sourceFragment.instanceKey !== item.containerInstanceId ||
        item.sourceFragment.fragmentKey !== `quote-row:${item.itemId}` ||
        item.fragmentAllocation !== undefined
      )
        throw new Error('报价自定义行与容器实例身份不一致');
      continue;
    }
    const allocation = item.fragmentAllocation;
    assertFragmentAllocation(allocation, item.sourceFragment, item.quantity);
    if (
      !allocation ||
      (allocation as QuoteFragmentAllocation).version !== 'quantity-cent-v1' ||
      allocation.instanceKey !== item.sourceFragment.instanceKey ||
      allocation.fragmentKey !== item.sourceFragment.fragmentKey ||
      allocation.relationKey !== item.sourceFragment.memberKey ||
      allocation.relationQuantity !== item.packageRelationQuantitySnapshot ||
      allocation.wholeQuantity !==
        allocation.relationQuantity * item.sourceFragment.instanceQuantity ||
      [
        allocation.relationQuantity,
        allocation.wholeQuantity,
        item.quantity,
      ].some((value) => !Number.isSafeInteger(value) || value < 1) ||
      [
        allocation.wholeRentalLineCents,
        allocation.allocatedRentalLineCents,
      ].some((value) => !Number.isSafeInteger(value) || value < 0)
    )
      throw new Error('分行数量或正式金额事实不一致');
    if (item.sourceFragment.mode === 'complete')
      instances.set(item.sourceFragment.instanceKey, [
        ...(instances.get(item.sourceFragment.instanceKey) ?? []),
        item,
      ]);
  }
  for (const group of instances.values()) {
    const first = group[0];
    const source = JSON.stringify([
      first.sourceType,
      first.sourceId,
      first.sourceRevisionId,
      (first.sourceFragment as QuoteSourceFragment).instanceQuantity,
    ]);
    const keys = new Set<string>();
    const members = new Map<string, QuoteItem[]>();
    for (const item of group) {
      if (
        JSON.stringify([
          item.sourceType,
          item.sourceId,
          item.sourceRevisionId,
          (item.sourceFragment as QuoteSourceFragment).instanceQuantity,
        ]) !== source ||
        keys.has((item.sourceFragment as QuoteSourceFragment).fragmentKey)
      )
        throw new Error('分行实例混入其他来源或重复分段');
      keys.add((item.sourceFragment as QuoteSourceFragment).fragmentKey);
      members.set((item.sourceFragment as QuoteSourceFragment).memberKey, [
        ...(members.get(
          (item.sourceFragment as QuoteSourceFragment).memberKey,
        ) ?? []),
        item,
      ]);
    }
    for (const parts of members.values()) {
      const allocation = parts[0].fragmentAllocation as QuoteFragmentAllocation;
      if (
        parts.some(
          (part) =>
            (part.fragmentAllocation as QuoteFragmentAllocation)
              .wholeQuantity !== allocation.wholeQuantity ||
            (part.fragmentAllocation as QuoteFragmentAllocation)
              .wholeRentalLineCents !== allocation.wholeRentalLineCents,
        ) ||
        parts.reduce((sum, part) => sum + part.quantity, 0) !==
          allocation.wholeQuantity
      )
        throw new Error('分行实例关系数量不完整');
      const expected = allocateFragmentCents(
        allocation.wholeRentalLineCents,
        parts.map((part) => ({
          key: (part.sourceFragment as QuoteSourceFragment).fragmentKey,
          quantity: part.quantity,
        })),
      );
      if (
        parts.some(
          (part) =>
            expected.get(
              (part.sourceFragment as QuoteSourceFragment).fragmentKey,
            ) !==
            (part.fragmentAllocation as QuoteFragmentAllocation)
              .allocatedRentalLineCents,
        )
      )
        throw new Error('分行正式金额分配不一致');
    }
  }
}

/** New instances must include every exact catalogue member; existing instances cannot silently drop one. */
export function assertFragmentMemberCoverage(
  items: readonly QuoteItem[],
  priorItems: readonly QuoteItem[],
  catalog: CpqState['catalog'],
): void {
  const instances = new Map<string, QuoteItem[]>();
  for (const item of items)
    if (item.sourceFragment?.mode === 'complete')
      instances.set(item.sourceFragment.instanceKey, [
        ...(instances.get(item.sourceFragment.instanceKey) ?? []),
        item,
      ]);
  for (const [key, group] of instances) {
    const first = group[0];
    const prior = priorItems.filter(
      (item) => item.sourceFragment?.instanceKey === key,
    );
    let expected: string[];
    if (prior.length)
      expected = prior.map(
        (item) => (item.sourceFragment as QuoteSourceFragment).memberKey,
      );
    else if (first.sourceType === 'package') {
      const pkg = catalog.packages.find(
        (entry) =>
          entry.id === first.sourceId &&
          entry.currentRevisionId === first.sourceRevisionId,
      );
      if (!pkg) throw new Error('新分行实例缺少准确套餐目录');
      expected = pkg.productLinks.map((relation) =>
        fragmentMemberKey({ ...first, packageRelationId: relation.relationId }),
      );
    } else {
      const plan = catalog.plans.find(
        (entry) =>
          entry.id === first.sourceId &&
          entry.currentRevisionId === first.sourceRevisionId,
      );
      if (!plan) throw new Error('新分行实例缺少准确方案目录');
      expected = plan.occurrenceModel
        ? plan.occurrenceModel.occurrences.flatMap((entry) =>
            entry.items.map((item) =>
              fragmentMemberKey({
                ...first,
                formalPlanOccurrence: {
                  occurrenceKey: entry.occurrenceKey,
                  itemKey: item.itemKey,
                },
              }),
            ),
          )
        : (plan.formalPackages ?? []).flatMap((pkg) =>
            pkg.items.map((relation) =>
              fragmentMemberKey({
                ...first,
                formalPlanOccurrence: undefined,
                planPackageMembershipId: pkg.planPackageMembershipId,
                packageRelationId: relation.packageRelationId,
              }),
            ),
          );
    }
    const actual = new Set(
      group.map(
        (item) => (item.sourceFragment as QuoteSourceFragment).memberKey,
      ),
    );
    const required = new Set(expected);
    if (
      required.size === 0 ||
      required.size !== actual.size ||
      [...required].some((member) => !actual.has(member))
    )
      throw new Error('分行实例缺少完整的正式目标成员');
  }
}

export function fragmentPackageQuantity(
  items: readonly QuoteItem[],
): number | null {
  const customized = items[0]?.sourceFragment;
  if (
    items.length > 0 &&
    customized?.mode === 'quote-customized' &&
    items.every(
      (item) =>
        item.sourceFragment?.mode === 'quote-customized' &&
        item.sourceFragment.instanceKey === customized.instanceKey &&
        item.sourceFragment.instanceQuantity === customized.instanceQuantity &&
        item.containerInstanceId === customized.instanceKey &&
        quoteTableGroupKey(item) === quoteTableGroupKey(items[0]),
    )
  ) {
    try {
      assertQuoteFragmentInstances(items);
      return customized.instanceQuantity;
    } catch {
      return null;
    }
  }
  if (
    items.length === 0 ||
    !items.every((item) => item.sourceFragment?.mode === 'complete')
  )
    return null;
  const first = items[0];
  if (
    items.some(
      (item) =>
        quoteTableGroupKey(item) !== quoteTableGroupKey(first) ||
        (item.sourceFragment as QuoteSourceFragment).instanceQuantity !==
          (first.sourceFragment as QuoteSourceFragment).instanceQuantity,
    )
  )
    return null;
  try {
    assertQuoteFragmentInstances(items);
    return (first.sourceFragment as QuoteSourceFragment).instanceQuantity;
  } catch {
    return null;
  }
}

/** Rescale an entire valid slice group; per-slice quantity never becomes a whole relation. */
export function resizeQuoteFragmentGroup(
  items: readonly QuoteItem[],
  sets: number,
): QuoteItem[] {
  assertQuoteFragmentInstances(items);
  if (!Number.isSafeInteger(sets) || sets < 1)
    throw new Error('分行套餐套数无效');
  const firstFragment = items[0].sourceFragment as QuoteSourceFragment;
  const priorSets = firstFragment.instanceQuantity;
  if (firstFragment.mode === 'quote-customized') {
    if (
      !items.every(
        (item) =>
          item.containerInstanceId === firstFragment.instanceKey &&
          item.sourceFragment?.mode === 'quote-customized' &&
          item.sourceFragment.instanceKey === firstFragment.instanceKey &&
          item.sourceFragment.instanceQuantity === priorSets,
      )
    )
      throw new Error('报价自定义套餐实例身份不一致，已阻止数量匹配');
    return items.map((item) => {
      if (item.quantity % priorSets !== 0)
        throw new Error('报价自定义成员数量无法按整套缩放');
      const quantity = (item.quantity / priorSets) * sets;
      if (!Number.isSafeInteger(quantity) || quantity < 1)
        throw new Error('报价自定义成员数量超出安全范围');
      return {
        ...item,
        quantity,
        ...(item.planProductSubtotalSnapshot !== undefined &&
        item.planProductPriceSnapshot !== undefined
          ? {
              planProductSubtotalSnapshot:
                item.planProductPriceSnapshot * quantity,
            }
          : {}),
        sourceFragment: {
          ...firstFragment,
          instanceQuantity: sets,
        },
        fragmentAllocation: undefined,
      };
    });
  }
  const members = new Map<string, QuoteItem[]>();
  for (const item of items)
    members.set((item.sourceFragment as QuoteSourceFragment).memberKey, [
      ...(members.get((item.sourceFragment as QuoteSourceFragment).memberKey) ??
        []),
      item,
    ]);
  const changed = new Map<string, QuoteItem>();
  for (const parts of members.values()) {
    const prior = parts[0].fragmentAllocation as QuoteFragmentAllocation;
    if (prior.wholeRentalLineCents % priorSets !== 0)
      throw new Error('正式来源金额无法按整套缩放');
    const quantities = parts.map((part) => {
      if (part.quantity % priorSets !== 0)
        throw new Error('分行数量无法按整套缩放');
      const quantity = (part.quantity / priorSets) * sets;
      if (!Number.isSafeInteger(quantity))
        throw new Error('分行数量超出安全范围');
      return {
        key: (part.sourceFragment as QuoteSourceFragment).fragmentKey,
        quantity,
      };
    });
    const total = (prior.wholeRentalLineCents / priorSets) * sets;
    const amounts = allocateFragmentCents(total, quantities);
    const priorOriginal = parts.every(
      (part) =>
        part.fragmentAllocation?.allocatedPackageOriginalLineCents !==
        undefined,
    )
      ? parts.reduce(
          (sum, part) =>
            sum +
            ((part.fragmentAllocation as QuoteFragmentAllocation)
              .allocatedPackageOriginalLineCents as number),
          0,
        )
      : undefined;
    if (priorOriginal !== undefined && priorOriginal % priorSets !== 0)
      throw new Error('套餐原价无法按整套缩放');
    const originalAmounts =
      priorOriginal === undefined
        ? undefined
        : allocateFragmentCents((priorOriginal / priorSets) * sets, quantities);
    for (const [index, part] of parts.entries())
      changed.set(part.itemId, {
        ...part,
        quantity: quantities[index].quantity,
        ...(part.planProductSubtotalSnapshot !== undefined
          ? { planProductSubtotalSnapshot: total / 100 }
          : {}),
        ...(part.formalPlanOccurrence
          ? {
              formalPlanOccurrence: {
                ...part.formalPlanOccurrence,
                sourceLineCents: total,
              },
            }
          : {}),
        sourceFragment: {
          ...(part.sourceFragment as QuoteSourceFragment),
          instanceQuantity: sets,
        },
        fragmentAllocation: {
          ...(part.fragmentAllocation as QuoteFragmentAllocation),
          wholeQuantity: prior.relationQuantity * sets,
          wholeRentalLineCents: total,
          allocatedRentalLineCents: amounts.get(
            (part.sourceFragment as QuoteSourceFragment).fragmentKey,
          ) as number,
          ...(originalAmounts
            ? {
                allocatedPackageOriginalLineCents: originalAmounts.get(
                  (part.sourceFragment as QuoteSourceFragment).fragmentKey,
                ) as number,
              }
            : {}),
        },
      });
  }
  return items.map((item) => changed.get(item.itemId) as QuoteItem);
}
